import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, LogOut, X, Lock, TrendingUp, Truck, CheckCircle2, AlertCircle, Users, IndianRupee, Filter, Calendar, Box, Clock, MapPin, UserPlus, Trash2, Inbox, Phone, UserCog, Search, Printer, Eye, Image as ImageIcon, Tag, Shield, Ruler, Weight, Route } from 'lucide-react';
import AdminPickupRouteMap from '../components/AdminPickupRouteMap';
import { BrandMark } from '../components/BrandMark';
import { InstallAppButton } from '../components/InstallAppButton';
import { ViewMapButton } from '../components/ViewMapButton';
import { CallCustomerButton } from '../components/CallCustomerButton';
import { NotificationSettings } from '../components/NotificationSettings';
import { FindBagPanel } from '../components/FindBagPanel';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import { InvoiceResendCard } from '../components/InvoiceResendCard';
import { ReportsExportCard } from '../components/ReportsExportCard';
import { RegisteredUsersTab } from '../components/RegisteredUsersTab';
import { PendingPaymentsTab } from '../components/PendingPaymentsTab';
import { RecalculatePaymentCard } from '../components/RecalculatePaymentCard';
import { InsuranceConsentPanel } from '../components/InsuranceConsentPanel';
import { RefundErrorNote } from '../components/RefundErrorNote';
import { formatInr } from '../lib/formatInr';
import { displayAmount, pendingAmountNote } from '../lib/bookingAmount';
import { PurgeOrdersCard } from '../components/PurgeOrdersCard';
import BaggageTagPrint, { triggerPrint } from '../components/BaggageTagPrint';
import { getHomePath } from '../lib/homeNav';
import { setAdminUiFlag, clearAdminUiFlag, hasAdminUiFlag } from '../lib/adminAuth';
import { formatPickupSlot } from '../lib/pickupSlots';
import { formatPickupAddress, formatDeliveryAddress } from '../lib/addressFormat';
import axios from 'axios';

const last10 = (v) => String(v || '').replace(/\D/g, '').slice(-10);

const refundResultText = (d) => {
  const r = d.refund || {};
  const via = r.provider === 'razorpay' ? ' via Razorpay (reaches customer in 5–7 working days)' : '';
  const ref = r.refund_id ? ` Ref: ${r.refund_id}.` : '';
  const warn = d.partial_error ? ` Only part could be refunded: ${d.partial_error}` : '';
  return `Refund of ₹${Number(r.amount || 0).toFixed(2)} sent${via}.${ref} Total refunded: ₹${Number(d.total_refunded || 0).toFixed(2)}.${warn}`;
};

// Mirrors backend _send_delivery_otp: delivery_phone first, else customer phone.
const DeliveryOtpNumber = ({ booking }) => {
  const delivery = last10(booking.delivery_phone);
  const fallback = last10(booking.customer_phone) || last10(booking.user_mobile);
  const phone = delivery.length === 10 ? delivery : (fallback.length === 10 ? fallback : '');
  const source = delivery.length === 10 ? 'DELIVERY CONTACT' : 'CUSTOMER ACCOUNT';
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap pt-2 border-t border-dashed border-gray-300" data-testid="admin-delivery-otp-number">
      <div>
        <span className="font-bold inline-flex items-center gap-1"><Phone size={12}/>DELIVERY OTP NUMBER —</span>{' '}
        {phone ? (
          <>
            <span className="font-bold whitespace-nowrap" data-testid="admin-delivery-otp-phone">+91 {phone}</span>
            <span className="text-gray-600 text-xs"> ({source})</span>
          </>
        ) : (
          <span className="text-red-600" data-testid="admin-delivery-otp-missing">Not available — edit booking to add a delivery phone</span>
        )}
      </div>
      {phone && <CallCustomerButton phone={phone} size="sm" />}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ensure axios includes the httpOnly admin_session cookie on every request.
// Set once at module scope — inherited by axios calls from sub-components.
axios.defaults.withCredentials = true;

const STATUS_CONFIG = {
  NEW_BOOKING: { color: '#A5B4FC', label: 'NEW BOOKING', icon: Calendar },
  BOOKED: { color: '#52525B', label: 'BOOKED', icon: Calendar },
  PAID: { color: '#00C853', label: 'PAID', icon: CheckCircle2 },
  SCHEDULED: { color: '#FFB800', label: 'SCHEDULED', icon: Calendar },
  DELAYED_PICKUP: { color: '#DC2626', label: 'DELAYED PICKUP', icon: AlertCircle },
  PICKED_UP: { color: '#FFB800', label: 'PICKED UP', icon: Box },
  IN_TRANSIT: { color: '#FF4500', label: 'IN TRANSIT', icon: Truck },
  OUT_FOR_DELIVERY: { color: '#FF4500', label: 'OUT FOR DELIVERY', icon: Truck },
  DELAYED_DELIVERY: { color: '#DC2626', label: 'DELAYED DELIVERY', icon: AlertCircle },
  DELIVERED: { color: '#00C853', label: 'DELIVERED', icon: CheckCircle2 },
  DELAYED: { color: '#DC2626', label: 'DELAYED', icon: AlertCircle },
  REROUTED: { color: '#F59E0B', label: 'REROUTED', icon: AlertCircle },
  CANCELLED_BY_CUSTOMER: { color: '#9CA3AF', label: 'CANCELLED BY CUSTOMER', icon: AlertCircle }
};

const STATUS_OPTIONS = ['ALL', 'NEW_BOOKING', 'BOOKED', 'PAID', 'SCHEDULED', 'DELAYED_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY', 'DELIVERED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'];
// Admin can override-set any status (still keeps full picker excluding PAID)
const STATUS_PICKER_OPTIONS = ['NEW_BOOKING', 'BOOKED', 'SCHEDULED', 'DELAYED_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY', 'DELIVERED', 'REROUTED', 'CANCELLED_BY_CUSTOMER'];

const Admin = () => {
  const navigate = useNavigate();
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();
  const [isAuth, setIsAuth] = useState(() => hasAdminUiFlag());
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Latest-value ref so fetchData always sees the current searchQuery without
  // having to re-create its useCallback on every keystroke.
  const searchQueryRef = useRef('');
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  // Centralised booking-search predicate. Supports:
  //   • baggage tag (LFJ123456)
  //   • full booking_id or any UUID prefix
  //   • razorpay_payment_id (pay_XXXX…) and razorpay_order_id (order_XXXX…)
  //   • customer name, mobile, email
  // Case-insensitive, substring match.
  const matchesSearch = useCallback((b) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const haystacks = [
      b.baggage_tag,
      b.booking_id,
      b.razorpay_payment_id,
      b.razorpay_order_id,
      b.customer_name,
      b.user_mobile,
      b.user_email,
    ];
    return haystacks.some(v => (v || '').toString().toLowerCase().includes(q));
  }, [searchQuery]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' | 'partners' | 'agents'
  
  // Partners
  const [partners, setPartners] = useState([]);
  const [showCreatePartner, setShowCreatePartner] = useState(false);
  const [newPartner, setNewPartner] = useState({ partner_code: '', name: '', contact: '', password: '' });
  const [partnerError, setPartnerError] = useState('');
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [editPartnerData, setEditPartnerData] = useState({ name: '', contact: '' });
  const [resetPwPartner, setResetPwPartner] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Agents
  const [agents, setAgents] = useState([]);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [newAgent, setNewAgent] = useState({ agent_code: '', name: '', contact: '', password: '' });
  const [agentError, setAgentError] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [resetPwAgent, setResetPwAgent] = useState(null);
  const [newAgentPassword, setNewAgentPassword] = useState('');
  
  // Bulk assign
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [bulkAssignValue, setBulkAssignValue] = useState(''); // format: "partner:CODE" or "agent:CODE" or ""
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Printable tag
  const [printingBooking, setPrintingBooking] = useState(null);
  
  // View bag details modal
  const [viewingBooking, setViewingBooking] = useState(null);
  
  const handlePrint = async (booking) => {
    // Try to fetch full customer info (aadhaar) for the printable tag
    let customer = null;
    if (booking.user_id) {
      try {
        const r = await axios.get(`${API}/users/${booking.user_id}`);
        customer = r.data;
      } catch (e) { console.warn('[admin] fetch customer for print failed:', e); }
    }
    setPrintingBooking({ booking, customer });
    // Wait for re-render then trigger print
    setTimeout(() => {
      triggerPrint();
      setTimeout(() => setPrintingBooking(null), 1000);
    }, 250);
  };

  // Open booking details — also seed the refund amount field with the remaining balance
  const openBookingDetails = (b) => {
    setViewingBooking(b);
    const total = Number(b.amount || 0);
    const refunded = Number(b.total_refunded || 0);
    const remaining = Math.max(0, +(total - refunded).toFixed(2));
    setRefundAmount(remaining ? String(remaining) : '');
    setRefundNotes('');
    setRefundError('');
    setRefundSuccess('');
  };

  const closeBookingDetails = () => {
    setViewingBooking(null);
    setRefundError('');
    setRefundSuccess('');
    setAadhaarRevealOpen(false);
    setAadhaarReason('');
    setAadhaarRevealed('');
    setAadhaarError('');
  };

  // Escape-key dismiss for the "View bag details" modal — matches the click-
  // outside overlay handler, so keyboard users can always exit even when the
  // modal contents are taller than the viewport.
  useEffect(() => {
    if (!viewingBooking) return;
    const onKey = (e) => { if (e.key === 'Escape') closeBookingDetails(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewingBooking]);

  const handleRevealAadhaar = async () => {
    setAadhaarError('');
    if (!aadhaarReason.trim() || aadhaarReason.trim().length < 5) {
      setAadhaarError('Please enter a reason of at least 5 characters');
      return;
    }
    if (!viewingBooking?.user_id) return;
    setAadhaarLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/aadhaar-reveal`,
        { user_id: viewingBooking.user_id, reason: aadhaarReason.trim() },
      );
      setAadhaarRevealed(res.data.aadhaar_number || '');
    } catch (err) {
      setAadhaarError(err.response?.data?.detail || 'Failed to reveal Aadhaar');
    } finally {
      setAadhaarLoading(false);
    }
  };

  // Inline per-row refund handler used by the Refunds tab
  const handleRowRefund = async (booking) => {
    const bid = booking.booking_id;
    setRowRefundMsg((p) => ({ ...p, [bid]: null }));
    // Compute the same "remaining" the input auto-populates with — so a click
    // on REFUND without typing anything still submits the correct amount.
    const cancelled = booking.tracking_status === 'CANCELLED_BY_CUSTOMER';
    const isEditRefund = !cancelled && booking.payment_status === 'refund_pending' && Number(booking.pending_refund_amount || 0) > 0;
    const baseline = isEditRefund
      ? Number(booking.pending_refund_amount || 0)
      : displayAmount(booking);
    const refundedSoFar = Number(booking.total_refunded || 0);
    const autoRemaining = Math.max(0, +(baseline - refundedSoFar).toFixed(2));

    const raw = rowRefundAmounts[bid];
    const amt = raw === undefined || raw === '' ? autoRemaining : parseFloat(raw);
    if (!amt || amt <= 0) {
      setRowRefundMsg((p) => ({ ...p, [bid]: { type: 'err', text: 'Enter a valid amount > 0' } }));
      return;
    }
    setRowRefundLoading((p) => ({ ...p, [bid]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/admin/bookings/${bid}/refund`,
        { amount: amt, notes: (rowRefundNotes[bid] || '').trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRowRefundMsg((p) => ({
        ...p,
        [bid]: {
          type: 'ok',
          text: refundResultText(res.data)
        }
      }));
      setRowRefundAmounts((p) => ({ ...p, [bid]: '' }));
      setRowRefundNotes((p) => ({ ...p, [bid]: '' }));
      await fetchData();
    } catch (err) {
      setRowRefundMsg((p) => ({
        ...p,
        [bid]: { type: 'err', text: err.response?.data?.detail || 'Refund failed' }
      }));
    } finally {
      setRowRefundLoading((p) => ({ ...p, [bid]: false }));
    }
  };

  const handleProcessRefund = async () => {
    if (!viewingBooking) return;    setRefundError('');
    setRefundSuccess('');
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) {
      setRefundError('Enter a refund amount greater than zero');
      return;
    }
    setRefundLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/bookings/${viewingBooking.booking_id}/refund`,
        { amount: amt, notes: refundNotes.trim() },
      );
      setRefundSuccess(refundResultText(res.data));
      // Refresh: refetch list and update the open booking with new fields
      await fetchData();
      setViewingBooking((prev) => prev ? {
        ...prev,
        payment_status: res.data.payment_status,
        total_refunded: res.data.total_refunded,
        refunds: [...(prev.refunds || []), res.data.refund]
      } : prev);
      const newRemaining = Math.max(0, +((viewingBooking.amount || 0) - (res.data.total_refunded || 0)).toFixed(2));
      setRefundAmount(newRemaining ? String(newRemaining) : '');
      setRefundNotes('');
    } catch (err) {
      setRefundError(err.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundLoading(false);
    }
  };

  // Update status modal
  const [editBooking, setEditBooking] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusLocation, setStatusLocation] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  // Aadhaar reveal modal (gated by reason + audit log)
  const [aadhaarRevealOpen, setAadhaarRevealOpen] = useState(false);
  const [aadhaarReason, setAadhaarReason] = useState('');
  const [aadhaarLoading, setAadhaarLoading] = useState(false);
  const [aadhaarRevealed, setAadhaarRevealed] = useState('');
  const [aadhaarError, setAadhaarError] = useState('');

  // Refunds (tab) — per-row inline state
  const [rowRefundAmounts, setRowRefundAmounts] = useState({}); // {booking_id: '500'}
  const [rowRefundNotes, setRowRefundNotes] = useState({});     // {booking_id: 'note'}
  const [rowRefundLoading, setRowRefundLoading] = useState({}); // {booking_id: true}
  const [rowRefundMsg, setRowRefundMsg] = useState({});         // {booking_id: {type:'ok'|'err', text:''}}
  const [refundsSearch, setRefundsSearch] = useState('');

  // Refund (admin) state for the View-Details modal
  const [refundAmount, setRefundAmount] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [refundSuccess, setRefundSuccess] = useState('');

  useEffect(() => {
    // On mount, verify server-side that the httpOnly cookie is still valid.
    // Falls back to the legacy sessionStorage / localStorage flag for a
    // fast pre-render decision; the server response is the source of truth.
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/session/check`);
        if (!cancelled) {
          setAdminUiFlag();  // refresh flag so router picks /admin on Home
          setIsAuth(true);
        }
      } catch (e) {
        if (!cancelled) {
          clearAdminUiFlag();
          setIsAuth(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchAdminBooking = async (id) => {
    const res = await axios.get(`${API}/admin/bookings?q=${encodeURIComponent(id)}`);
    return (res.data.bookings || []).find((x) => x.booking_id === id) || null;
  };

  const refreshViewingBooking = async () => {
    if (!viewingBooking) return;
    const fresh = await fetchAdminBooking(viewingBooking.booking_id);
    if (fresh) setViewingBooking(fresh);
    fetchData();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const trimmedQuery = (searchQueryRef.current || '').trim();
      if (trimmedQuery) {
        // Global search bypasses status/unassigned filters so support can
        // find a booking regardless of which tab is active.
        params.append('q', trimmedQuery);
      } else {
        if (statusFilter !== 'ALL') params.append('status', statusFilter);
        if (showUnassignedOnly) params.append('unassigned', 'true');
      }
      const queryStr = params.toString() ? `?${params.toString()}` : '';

      const [statsRes, bookingsRes, partnersRes, agentsRes, unassignedRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings${queryStr}`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/agents`),
        axios.get(`${API}/admin/stats/unassigned-count`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
      setPartners(partnersRes.data.partners);
      setAgents(agentsRes.data.agents);
      setUnassignedCount(unassignedRes.data.unassigned_count || 0);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showUnassignedOnly]);

  useEffect(() => {
    if (isAuth) fetchData();
  }, [isAuth, fetchData]);

  // Debounce admin search box so typing doesn't fire a request per keystroke.
  // Skip the very first render — fetchData() has already fired via the effect above.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!isAuth) return;
    if (!didMountRef.current) { didMountRef.current = true; return; }
    const t = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Support tickets
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportCounts, setSupportCounts] = useState({ open: 0, replied: 0, closed: 0, total: 0 });
  const [supportStatusFilter, setSupportStatusFilter] = useState('open');
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportViewTicket, setSupportViewTicket] = useState(null);

  const fetchSupportTickets = useCallback(async () => {
    setSupportLoading(true);
    try {
      const params = supportStatusFilter && supportStatusFilter !== 'all'
        ? `?status_filter=${supportStatusFilter}`
        : '';
      const res = await axios.get(`${API}/admin/support/tickets${params}`);
      setSupportTickets(res.data.tickets || []);
      setSupportCounts(res.data.counts || { open: 0, replied: 0, closed: 0, total: 0 });
    } catch (err) {
      console.error('Failed to fetch support tickets:', err);
    } finally {
      setSupportLoading(false);
    }
  }, [supportStatusFilter]);

  useEffect(() => {
    if (isAuth && activeTab === 'support') fetchSupportTickets();
  }, [isAuth, activeTab, fetchSupportTickets]);

  const handleSupportStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticketId}`, { status: newStatus });
      await fetchSupportTickets();
      if (supportViewTicket?.ticket_id === ticketId) {
        setSupportViewTicket(prev => prev ? { ...prev, status: newStatus } : prev);
      }
    } catch (err) {
      console.error('Failed to update ticket status:', err);
    }
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuth(true);
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuth(false);
    setPassword('');
  };

  const openUpdateModal = (booking) => {
    // Issue 5 — alert the operator that they're about to modify a booking
    // that is still in BOOKED state (awaiting payment / pickup scheduling).
    if ((booking.tracking_status || 'BOOKED') === 'BOOKED') {
      const ok = window.confirm(
        `Heads up: Booking ${booking.baggage_tag || booking.booking_id} is still in BOOKED status (awaiting payment / scheduling).\n\nAny update you make now may surprise the customer. Continue?`
      );
      if (!ok) return;
    }
    setEditBooking(booking);
    setNewStatus(booking.tracking_status || 'BOOKED');
    setStatusLocation('');
    setStatusNotes('');
    setUpdateError('');
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    setUpdateError('');
    try {
      await axios.post(`${API}/bookings/${editBooking.booking_id}/status`, {
        status: newStatus,
        location: statusLocation,
        notes: statusNotes
      });
      setEditBooking(null);
      fetchData();
    } catch (err) {
      setUpdateError(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignUnified = async (bookingId, value) => {
    // value format: "" | "partner:CODE" | "agent:CODE"
    let assignee_type = null;
    let assignee_code = '';
    if (value) {
      const [t, c] = value.split(':');
      assignee_type = t;
      assignee_code = c;
    }
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/assign-v2`, {
        assignee_type,
        assignee_code
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleCreateAgent = async () => {
    setAgentLoading(true);
    setAgentError('');
    try {
      await axios.post(`${API}/admin/agents`, newAgent);
      setShowCreateAgent(false);
      setNewAgent({ agent_code: '', name: '', contact: '', password: '' });
      fetchData();
    } catch (err) {
      setAgentError(err.response?.data?.detail || 'Failed to create agent');
    } finally {
      setAgentLoading(false);
    }
  };

  const handleDeactivateAgent = async (agentId) => {
    if (!window.confirm('Deactivate this agent? They will no longer be able to login.')) return;
    try {
      await axios.delete(`${API}/admin/agents/${agentId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleResetAgentPassword = async () => {
    if (newAgentPassword.length < 4) {
      alert('Password must be at least 4 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/agents/${resetPwAgent.agent_id}/reset-password`, { new_password: newAgentPassword });
      setResetPwAgent(null);
      setNewAgentPassword('');
      alert(`Password reset successfully for ${resetPwAgent.agent_code}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleCreatePartner = async () => {
    setPartnerLoading(true);
    setPartnerError('');
    try {
      await axios.post(`${API}/admin/partners`, newPartner);
      setShowCreatePartner(false);
      setNewPartner({ partner_code: '', name: '', contact: '', password: '' });
      fetchData();
    } catch (err) {
      setPartnerError(err.response?.data?.detail || 'Failed to create partner');
    } finally {
      setPartnerLoading(false);
    }
  };

  const handleDeactivatePartner = async (partnerId) => {
    if (!window.confirm('Deactivate this partner? They will no longer be able to login.')) return;
    try {
      await axios.delete(`${API}/admin/partners/${partnerId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const openEditPartner = (partner) => {
    setEditPartner(partner);
    setEditPartnerData({ name: partner.name, contact: partner.contact || '' });
  };

  const handleSaveEditPartner = async () => {
    try {
      await axios.put(`${API}/admin/partners/${editPartner.partner_id}`, editPartnerData);
      setEditPartner(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 4) {
      alert('Password must be at least 4 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/partners/${resetPwPartner.partner_id}/reset-password`, { new_password: newPassword });
      setResetPwPartner(null);
      setNewPassword('');
      alert(`Password reset successfully for ${resetPwPartner.partner_code}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBookings.size === bookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(bookings.map(b => b.booking_id)));
    }
  };

  const handleBulkAssign = async () => {
    let assignee_type = null;
    let assignee_code = '';
    if (bulkAssignValue) {
      const [t, c] = bulkAssignValue.split(':');
      assignee_type = t;
      assignee_code = c;
    }
    try {
      const res = await axios.post(`${API}/admin/bookings/bulk-assign-v2`, {
        booking_ids: Array.from(selectedBookings),
        assignee_type,
        assignee_code
      });
      const label = assignee_code ? `to ${assignee_type.toUpperCase()} ${assignee_code}` : 'unassigned';
      alert(`Successfully ${assignee_code ? 'assigned' : 'unassigned'} ${res.data.modified_count} booking${res.data.modified_count !== 1 ? 's' : ''} ${label}`);
      setSelectedBookings(new Set());
      setShowBulkModal(false);
      setBulkAssignValue('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to bulk assign');
    }
  };

  // Login Screen
  if (!isAuth) {
    return (
      <div className="theme-sunset flex items-center justify-center p-4">
        <div className="card-brutalist p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <BrandMark size={48} showText={false} className="justify-center mb-3" />
            <Lock size={36} color="#FF4500" strokeWidth={2} className="mx-auto mb-2" />
            <h1 className="text-2xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              ADMIN ACCESS
            </h1>
            <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
              RESTRICTED AREA
            </p>
          </div>
          <label className="block text-xs font-mono uppercase tracking-wide mb-2">PASSWORD</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Enter admin password"
            className="w-full mb-4"
          />
          {loginError && (
            <p data-testid="admin-login-error" className="text-red-600 text-sm mb-4 font-mono">{loginError}</p>
          )}
          <button
            data-testid="admin-login-btn"
            onClick={handleLogin}
            disabled={loginLoading}
            className="btn-primary w-full py-3 disabled:opacity-50"
          >
            {loginLoading ? 'AUTHENTICATING...' : 'LOGIN'}
          </button>
          <InstallAppButton className="mt-3" label="INSTALL ADMIN APP" manifest="/manifest-admin.json" />
        </div>
      </div>
    );
  }

  return (
    <div className="theme-sunset p-4 md:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BrandMark size={36} />
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="px-2 py-1 bg-black text-white text-xs font-mono uppercase">ADMIN</span>
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm">
              <LogOut size={16} />
              LOGOUT
            </button>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
            OPERATIONS PANEL
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
            MANAGE BOOKINGS, TRACK DELIVERIES, UPDATE STATUSES
          </p>
        </div>

        {/* Search bar — aligned right, above tabs */}
        <div className="flex justify-end mb-4">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              data-testid="admin-search-baggage-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="SEARCH BY TAG, BOOKING ID, RAZORPAY ID, NAME OR MOBILE"
              className="w-full pl-10 pr-9 py-2 font-mono text-xs uppercase tracking-wide"
            />
            {searchQuery && (
              <button
                data-testid="admin-search-clear-btn"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b-2 border-black flex-wrap">
          <button
            data-testid="tab-bookings"
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 ${
              activeTab === 'bookings' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            BOOKINGS & STATS
          </button>
          <button
            data-testid="tab-partners"
            onClick={() => setActiveTab('partners')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'partners' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            PARTNERS ({partners.length})
          </button>
          <button
            data-testid="tab-agents"
            onClick={() => setActiveTab('agents')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'agents' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            AGENTS ({agents.length})
          </button>
          <button
            data-testid="tab-pickups"
            onClick={() => setActiveTab('pickups')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 flex items-center gap-2 ${
              activeTab === 'pickups' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            <Route size={14} />
            PICKUP ROUTE
          </button>
          <button
            data-testid="tab-refunds"
            onClick={() => setActiveTab('refunds')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'refunds' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            REFUNDS ({bookings.filter(b => b.tracking_status === 'CANCELLED_BY_CUSTOMER' || b.payment_status === 'refund_pending').length})
          </button>
          <button
            data-testid="tab-support"
            onClick={() => setActiveTab('support')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'support' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            SUPPORT{supportCounts.open > 0 ? ` (${supportCounts.open})` : ''}
          </button>
          <button
            data-testid="tab-users"
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'users' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            REGISTERED USERS
          </button>
          <button
            data-testid="tab-pending-payments"
            onClick={() => setActiveTab('pending-payments')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'pending-payments' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            PENDING PAYMENTS
          </button>
          <button
            data-testid="tab-settings"
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 font-mono uppercase text-sm font-bold border-2 border-black border-b-0 border-l-0 ${
              activeTab === 'settings' ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            SETTINGS
          </button>
        </div>

        {activeTab === 'bookings' && (
        <>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <div className="card-brutalist p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={20} color="#FF4500" />
                <span className="text-xs font-mono uppercase tracking-wide text-gray-600">TOTAL BOOKINGS</span>
              </div>
              <div className="text-3xl font-black font-mono" data-testid="stat-total">{stats.total_bookings}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck size={20} color="#FF4500" />
                <span className="text-xs font-mono uppercase tracking-wide text-gray-600">ACTIVE DELIVERIES</span>
              </div>
              <div className="text-3xl font-black font-mono" data-testid="stat-active">{stats.active_deliveries}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={20} color="#00C853" />
                <span className="text-xs font-mono uppercase tracking-wide text-gray-600">DELIVERED</span>
              </div>
              <div className="text-3xl font-black font-mono" data-testid="stat-delivered">{stats.delivered}</div>
            </div>
            <div className="card-brutalist p-4">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee size={20} color="#FF4500" />
                <span className="text-xs font-mono uppercase tracking-wide text-gray-600">REVENUE</span>
              </div>
              <div className="text-3xl font-black font-mono" data-testid="stat-revenue">₹{stats.total_revenue.toFixed(0)}</div>
            </div>
          </div>
        )}

        {/* Secondary Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-brutalist p-3 flex items-center justify-between bg-[#FFF7E6]" data-testid="stat-unassigned-card">
              <div className="flex items-center gap-2">
                <Inbox size={16} color="#FF4500" />
                <span className="text-xs font-mono uppercase font-bold">NEW / UNASSIGNED</span>
              </div>
              <span className="font-black font-mono text-lg" style={{ color: '#FF4500' }} data-testid="stat-unassigned">{unassignedCount}</span>
            </div>
            <div className="card-brutalist p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} color="#52525B" />
                <span className="text-xs font-mono uppercase">REGISTERED USERS</span>
              </div>
              <span className="font-black font-mono">{stats.registered_users}</span>
            </div>
            <div className="card-brutalist p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} color="#00C853" />
                <span className="text-xs font-mono uppercase">PAID BOOKINGS</span>
              </div>
              <span className="font-black font-mono">{stats.paid_bookings}</span>
            </div>
            <div className="card-brutalist p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} color="#DC2626" />
                <span className="text-xs font-mono uppercase">DELAYED</span>
              </div>
              <span className="font-black font-mono">{stats.delayed}</span>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={16} />
            <span className="text-xs font-mono uppercase">FILTER:</span>
          </div>
          <button
            data-testid="filter-unassigned"
            onClick={() => setShowUnassignedOnly(!showUnassignedOnly)}
            className={`px-3 py-1 border-2 border-black text-xs font-mono uppercase font-bold flex items-center gap-1 ${
              showUnassignedOnly ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            <Inbox size={12} />
            NEW / UNASSIGNED ({unassignedCount})
          </button>
          <div className="h-6 w-px bg-black"></div>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt}
              data-testid={`filter-${opt.toLowerCase()}`}
              onClick={() => setStatusFilter(opt)}
              className={`px-3 py-1 border-2 border-black text-xs font-mono uppercase ${
                statusFilter === opt ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              {opt === 'ALL' ? 'ALL' : STATUS_CONFIG[opt]?.label || opt}
            </button>
          ))}
        </div>

        {/* Bookings Table */}
        <div className="card-brutalist p-0 overflow-hidden">
          <div className="px-6 py-4 border-b-2 border-black bg-white flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'IBM Plex Mono' }}>
              ALL BOOKINGS
            </h2>
            <div className="flex items-center gap-3">
              {selectedBookings.size > 0 && (
                <>
                  <span className="text-xs font-mono uppercase font-bold bg-[#FF4500] px-2 py-1 border-2 border-black" data-testid="selection-count">
                    {selectedBookings.size} SELECTED
                  </span>
                  <button
                    data-testid="bulk-assign-btn"
                    onClick={() => setShowBulkModal(true)}
                    className="btn-primary px-4 py-1.5 text-xs"
                  >
                    BULK ASSIGN
                  </button>
                  <button
                    onClick={() => setSelectedBookings(new Set())}
                    className="btn-secondary px-3 py-1.5 text-xs"
                  >
                    CLEAR
                  </button>
                </>
              )}
              <span className="text-xs font-mono uppercase text-gray-600">
                {searchQuery
                  ? `${bookings.filter(matchesSearch).length} of ${bookings.length}`
                  : `${bookings.length}`} RESULTS
              </span>
            </div>
          </div>
          {loading ? (
            <div className="p-12 text-center loading-pulse font-mono">LOADING...</div>
          ) : bookings.filter(matchesSearch).length === 0 ? (
            <div className="p-12 text-center font-mono text-gray-600" data-testid="no-bookings-message">
              {searchQuery ? `No bookings match "${searchQuery}".` : 'No bookings match this filter.'}
            </div>
          ) : (
            <div className="admin-scroll-x">
              <table className="w-full min-w-[1200px] text-sm" data-testid="bookings-table">
                <thead className="bg-gray-100 border-b-2 border-black">
                  <tr>
                    <th className="text-left p-3 w-10 sm:sticky sm:left-0 z-20 bg-gray-100 sm:shadow-[1px_0_0_rgba(0,0,0,0.08)]">
                      <input
                        data-testid="select-all-bookings"
                        type="checkbox"
                        checked={bookings.length > 0 && selectedBookings.size === bookings.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 border-2 border-black"
                      />
                    </th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide sm:sticky sm:left-[40px] z-20 bg-gray-100 min-w-[130px] max-w-[130px]">Tag</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide sm:sticky sm:left-[170px] z-20 bg-gray-100 min-w-[210px] max-w-[210px] sm:shadow-[2px_0_4px_rgba(0,0,0,0.06)]">Customer</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide min-w-[260px] max-w-[260px]">Route</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide whitespace-nowrap">Amount</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide whitespace-nowrap">Status</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide">Partner</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide">Agent</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide whitespace-nowrap">Created</th>
                    <th className="text-left p-3 font-mono uppercase text-xs tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.filter(matchesSearch).map((b) => {
                    const status = b.tracking_status || 'BOOKED';
                    const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
                    const isSelected = selectedBookings.has(b.booking_id);
                    return (
                      <tr key={b.booking_id} className={`border-b border-gray-200 ${isSelected ? 'bg-orange-50' : 'hover:bg-gray-50'}`} data-testid={`booking-row-${b.baggage_tag}`}>
                        <td className={`p-3 sm:sticky sm:left-0 z-10 ${isSelected ? 'bg-orange-50' : 'bg-white'} sm:shadow-[1px_0_0_rgba(0,0,0,0.06)]`}>
                          <input
                            data-testid={`select-booking-${b.baggage_tag}`}
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBookingSelection(b.booking_id)}
                            className="w-4 h-4 border-2 border-black"
                          />
                        </td>
                        <td className={`p-3 sm:sticky sm:left-[40px] z-10 min-w-[130px] max-w-[130px] ${isSelected ? 'bg-orange-50' : 'bg-white'}`}>
                          <div className="font-mono font-bold">{b.baggage_tag}</div>
                          <button
                            data-testid={`view-bag-details-${b.baggage_tag}`}
                            onClick={() => openBookingDetails(b)}
                            className="mt-1 px-2 py-0.5 border border-black text-[10px] font-mono uppercase hover:bg-black hover:text-white flex items-center gap-1"
                          >
                            <Eye size={9} />
                            DETAILS
                          </button>
                        </td>
                        <td className={`p-3 sm:sticky sm:left-[170px] z-10 min-w-[210px] max-w-[210px] ${isSelected ? 'bg-orange-50' : 'bg-white'} sm:shadow-[2px_0_4px_rgba(0,0,0,0.06)]`}>
                          <div className="font-medium">{b.customer_name}</div>
                          <div className="text-xs text-gray-500 font-mono">{b.user_mobile}</div>
                          {b.user_mobile && (
                            <CallCustomerButton phone={b.user_mobile} size="xs" className="mt-1" />
                          )}
                        </td>
                        <td className="p-3 text-xs min-w-[260px] max-w-[260px] align-top">
                          <div className="flex items-start justify-between gap-1 flex-wrap">
                            <div className="font-bold">{b.pickup_location || 'N/A'}</div>
                            <ViewMapButton booking={b} type="pickup" size="xs" />
                          </div>
                          <div className="text-gray-600 break-words whitespace-normal">{formatPickupAddress(b)}</div>
                          <div className="text-orange-600">↓</div>
                          <div className="flex items-start justify-between gap-1 flex-wrap">
                            <div className="font-bold">{b.delivery_location}</div>
                            <ViewMapButton booking={b} type="delivery" size="xs" />
                          </div>
                          {formatDeliveryAddress(b) && (
                            <div className="text-gray-600 break-words whitespace-normal">{formatDeliveryAddress(b)}</div>
                          )}
                        </td>
                        <td className="p-3 font-mono" data-testid={`admin-amount-${b.baggage_tag}`}>
                          <div className="font-bold">{formatInr(displayAmount(b))}</div>
                          {b.payment_status === 'top_up_pending' && b.tracking_status !== 'CANCELLED_BY_CUSTOMER' && (
                            <div className="text-[10px] font-bold text-red-700 whitespace-nowrap">DUE {formatInr(b.top_up_amount)}</div>
                          )}
                          {b.payment_status === 'refund_pending' && b.tracking_status !== 'CANCELLED_BY_CUSTOMER' && (
                            <div className="text-[10px] font-bold text-green-700 whitespace-nowrap">REFUND {formatInr(b.pending_refund_amount)}</div>
                          )}
                          {(() => {
                            const all = b.luggage_details?.bags || [];
                            const n = all.filter((x) => x.insurance_opted).length;
                            return n > 0 ? (
                              <div className="text-[10px] font-bold text-[#00A844] whitespace-nowrap" data-testid={`admin-insured-tag-${b.baggage_tag}`}>INSURED {n}/{all.length}</div>
                            ) : null;
                          })()}
                        </td>
                        <td className="p-3">
                          <div
                            className="inline-flex items-center gap-1 px-2 py-1 border-2 border-black text-xs font-bold uppercase"
                            style={{ backgroundColor: STATUS_CONFIG[status]?.color }}
                          >
                            <StatusIcon size={12} color="#000" />
                            {STATUS_CONFIG[status]?.label}
                          </div>
                        </td>
                        <td className="p-3">
                          <select
                            data-testid={`assign-partner-${b.baggage_tag}`}
                            value={b.partner_code || ''}
                            onChange={(e) => handleAssignUnified(b.booking_id, e.target.value ? `partner:${e.target.value}` : '')}
                            className="text-xs py-1 px-2 min-w-[120px]"
                          >
                            <option value="">— UNASSIGNED —</option>
                            {partners.filter(p => p.active).map(p => (
                              <option key={p.partner_id} value={p.partner_code}>{p.partner_code}</option>
                            ))}
                          </select>
                          {b.partner_name && (
                            <div className="text-[10px] font-mono text-gray-600 mt-1 truncate max-w-[140px]">{b.partner_name}</div>
                          )}
                        </td>
                        <td className="p-3">
                          {b.agent_code ? (
                            <div data-testid={`agent-info-${b.baggage_tag}`}>
                              <div className="flex items-center gap-1 mb-1">
                                <UserCog size={12} color="#FFB800" />
                                <span className="font-mono font-bold text-xs">{b.agent_code}</span>
                              </div>
                              {b.agent_name && (
                                <div className="text-xs font-medium truncate max-w-[140px]">{b.agent_name}</div>
                              )}
                              {b.agent_contact && (
                                <a href={`tel:${b.agent_contact}`} className="text-[10px] font-mono text-orange-600 hover:underline flex items-center gap-1">
                                  <Phone size={9} />{b.agent_contact}
                                </a>
                              )}
                              <button
                                data-testid={`clear-agent-${b.baggage_tag}`}
                                onClick={() => handleAssignUnified(b.booking_id, 'agent:')}
                                className="text-[10px] font-mono underline text-gray-500 hover:text-red-600 mt-1"
                                title="Clear agent (keep partner)"
                              >
                                clear
                              </button>
                            </div>
                          ) : (
                            <select
                              data-testid={`assign-agent-${b.baggage_tag}`}
                              value=""
                              onChange={(e) => e.target.value && handleAssignUnified(b.booking_id, `agent:${e.target.value}`)}
                              className="text-xs py-1 px-2 min-w-[120px]"
                            >
                              <option value="">— OVERRIDE: ASSIGN AGENT —</option>
                              {agents.filter(a => a.active).map(a => (
                                <option key={a.agent_id} value={a.agent_code}>{a.agent_code} — {a.name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="p-3 text-xs font-mono">
                          {new Date(b.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            <button
                              data-testid={`update-status-${b.baggage_tag}`}
                              onClick={() => openUpdateModal(b)}
                              className="btn-secondary px-3 py-1 text-xs"
                            >
                              UPDATE
                            </button>
                            <button
                              data-testid={`print-tag-${b.baggage_tag}`}
                              onClick={() => handlePrint(b)}
                              className="btn-secondary px-2 py-1 text-xs flex items-center gap-1 hover:bg-black hover:text-white"
                              title="Print baggage tag"
                            >
                              <Printer size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {/* PARTNERS TAB */}
        {activeTab === 'partners' && (
          <div data-testid="partners-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  DELIVERY PARTNERS
                </h2>
                <p className="text-xs text-gray-600 font-mono uppercase">
                  Manage logistics partners and their access
                </p>
              </div>
              <button
                data-testid="create-partner-btn"
                onClick={() => setShowCreatePartner(true)}
                className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
              >
                <UserPlus size={16} />
                ADD PARTNER
              </button>
            </div>

            {partners.length === 0 ? (
              <div className="card-brutalist p-12 text-center">
                <Users size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                <p className="font-mono text-gray-600">No partners registered yet.</p>
              </div>
            ) : (
              <div className="card-brutalist p-0 overflow-hidden">
                <div className="admin-scroll-x">
                  <table className="w-full min-w-[900px] text-sm whitespace-nowrap" data-testid="partners-table">
                    <thead className="bg-gray-100 border-b-2 border-black">
                      <tr>
                        <th className="text-left p-3 font-mono uppercase text-xs">Code</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Name</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Contact</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Assigned</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Status</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partners.map((p) => (
                        <tr key={p.partner_id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`partner-row-${p.partner_code}`}>
                          <td className="p-3 font-mono font-bold">{p.partner_code}</td>
                          <td className="p-3">{p.name}</td>
                          <td className="p-3 font-mono text-xs">{p.contact}</td>
                          <td className="p-3 font-mono font-bold">{p.assigned_bookings || 0}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 border-2 border-black text-xs font-bold uppercase ${p.active ? 'bg-green-100' : 'bg-gray-200 text-gray-500'}`}>
                              {p.active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {p.active && (
                                <>
                                  <button
                                    data-testid={`edit-partner-${p.partner_code}`}
                                    onClick={() => openEditPartner(p)}
                                    className="btn-secondary px-2 py-1 text-xs"
                                  >
                                    EDIT
                                  </button>
                                  <button
                                    data-testid={`reset-pw-${p.partner_code}`}
                                    onClick={() => { setResetPwPartner(p); setNewPassword(''); }}
                                    className="btn-secondary px-2 py-1 text-xs"
                                  >
                                    RESET PW
                                  </button>
                                  <button
                                    data-testid={`deactivate-partner-${p.partner_code}`}
                                    onClick={() => handleDeactivatePartner(p.partner_id)}
                                    className="btn-secondary px-2 py-1 text-xs flex items-center gap-1 hover:bg-red-600 hover:text-white"
                                  >
                                    <Trash2 size={10} />
                                    OFF
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* AGENTS TAB */}
        {activeTab === 'agents' && (
          <div data-testid="agents-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  FIELD AGENTS
                </h2>
                <p className="text-xs text-gray-600 font-mono uppercase">
                  Manage field agents who pick up &amp; update luggage status
                </p>
              </div>
              <button
                data-testid="create-agent-btn"
                onClick={() => setShowCreateAgent(true)}
                className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
              >
                <UserPlus size={16} />
                ADD AGENT
              </button>
            </div>

            {agents.length === 0 ? (
              <div className="card-brutalist p-12 text-center">
                <Users size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                <p className="font-mono text-gray-600">No agents registered yet.</p>
              </div>
            ) : (
              <div className="card-brutalist p-0 overflow-hidden">
                <div className="admin-scroll-x">
                  <table className="w-full min-w-[900px] text-sm whitespace-nowrap" data-testid="agents-table">
                    <thead className="bg-gray-100 border-b-2 border-black">
                      <tr>
                        <th className="text-left p-3 font-mono uppercase text-xs">Code</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Name</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Contact</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Assigned</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Status</th>
                        <th className="text-left p-3 font-mono uppercase text-xs">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((a) => (
                        <tr key={a.agent_id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`agent-row-${a.agent_code}`}>
                          <td className="p-3 font-mono font-bold">{a.agent_code}</td>
                          <td className="p-3">{a.name}</td>
                          <td className="p-3 font-mono text-xs">{a.contact}</td>
                          <td className="p-3 font-mono font-bold">{a.assigned_bookings || 0}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 border-2 border-black text-xs font-bold uppercase ${a.active ? 'bg-yellow-100' : 'bg-gray-200 text-gray-500'}`}>
                              {a.active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              {a.active && (
                                <>
                                  <button
                                    data-testid={`reset-agent-pw-${a.agent_code}`}
                                    onClick={() => { setResetPwAgent(a); setNewAgentPassword(''); }}
                                    className="btn-secondary px-2 py-1 text-xs"
                                  >
                                    RESET PW
                                  </button>
                                  <button
                                    data-testid={`deactivate-agent-${a.agent_code}`}
                                    onClick={() => handleDeactivateAgent(a.agent_id)}
                                    className="btn-secondary px-2 py-1 text-xs flex items-center gap-1 hover:bg-red-600 hover:text-white"
                                  >
                                    <Trash2 size={10} />
                                    OFF
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* REFUNDS TAB */}
        {/* PICKUP ROUTE TAB */}
        {activeTab === 'pickups' && (
          <div data-testid="pickups-tab">
            <AdminPickupRouteMap />
          </div>
        )}

        {activeTab === 'refunds' && (
          <div data-testid="refunds-tab">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  CANCELLED BOOKINGS & REFUNDS
                </h2>
                <p className="text-xs text-gray-600 font-mono uppercase">
                  Process refunds for customer-cancelled bookings · multiple partials allowed
                </p>
              </div>
              <input
                data-testid="refunds-search"
                type="text"
                value={refundsSearch}
                onChange={(e) => setRefundsSearch(e.target.value)}
                placeholder="Search tag / customer / mobile"
                className="px-3 py-2 text-sm max-w-xs w-full"
              />
            </div>

            {(() => {
              const q = refundsSearch.trim().toLowerCase();
              const cancelledBookings = bookings
                .filter(b => b.tracking_status === 'CANCELLED_BY_CUSTOMER' || b.payment_status === 'refund_pending')
                .filter(b => !q
                  || (b.baggage_tag || '').toLowerCase().includes(q)
                  || (b.customer_name || '').toLowerCase().includes(q)
                  || (b.user_mobile || '').toLowerCase().includes(q)
                );

              if (cancelledBookings.length === 0) {
                return (
                  <div className="card-brutalist p-12 text-center" data-testid="refunds-empty">
                    <CheckCircle2 size={48} strokeWidth={1.5} color="#52525B" className="mx-auto mb-3" />
                    <p className="font-mono text-gray-600 uppercase text-sm">
                      {q ? 'No bookings match your search.' : 'No refund requests pending.'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="space-y-4" data-testid="refunds-list">
                  {cancelledBookings.map((b) => {
                    // Edit-driven refunds carry the amount owed to the
                    // customer in `pending_refund_amount`. For cancellations
                    // the full paid `amount` is refundable. Pick the right
                    // "remaining" so the input box + button appear on both.
                    const cancelled = b.tracking_status === 'CANCELLED_BY_CUSTOMER';
                    const isEditRefund = !cancelled && b.payment_status === 'refund_pending' && Number(b.pending_refund_amount || 0) > 0;
                    const paidAmount = displayAmount(b);
                    const refunded = Number(b.total_refunded || 0);
                    const refundBaseline = isEditRefund
                      ? Number(b.pending_refund_amount || 0)
                      : paidAmount;
                    const remaining = Math.max(0, +(refundBaseline - refunded).toFixed(2));
                    const fullyRefunded = b.payment_status === 'refunded' || remaining <= 0;
                    // A booking is considered eligible for refund if it was paid
                    // OR if we've already started an edit-driven refund (which
                    // sets payment_status='refund_pending' — the money DID come
                    // in, it's just partially owed back).
                    const notPaid = !['paid', 'partially_refunded', 'refunded', 'refund_pending'].includes(b.payment_status);
                    const msg = rowRefundMsg[b.booking_id];
                    return (
                      <div key={b.booking_id} className="card-brutalist p-4 md:p-5" data-testid={`refund-row-${b.baggage_tag}`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Left — booking summary */}
                          <div>
                            <div className="data-label mb-1">BAGGAGE TAG</div>
                            <div className="text-xl font-black font-mono">{b.baggage_tag}</div>
                            <div className="text-[10px] font-mono text-gray-500 mt-0.5 break-all leading-tight" title={b.booking_id}>
                              ID: {b.booking_id}
                            </div>
                            {b.razorpay_payment_id && (
                              <div className="text-[10px] font-mono text-gray-500 mt-0.5 break-all leading-tight" title="Razorpay transaction id">
                                PAY: {b.razorpay_payment_id}
                              </div>
                            )}
                            <div className="mt-2 text-sm font-medium">{b.customer_name}</div>
                            {b.user_mobile && (
                              <a href={`tel:${b.user_mobile}`} className="text-xs font-mono text-orange-600 hover:underline flex items-center gap-1">
                                <Phone size={10} />{b.user_mobile}
                              </a>
                            )}
                            <div className="text-xs font-mono text-gray-600 mt-1">{b.pickup_location} → {b.delivery_location}</div>
                            {b.cancelled_at && (
                              <div className="text-[10px] font-mono text-gray-500 mt-1">
                                Cancelled {new Date(b.cancelled_at).toLocaleString('en-IN')}
                              </div>
                            )}
                          </div>

                          {/* Middle — reason + amounts */}
                          <div>
                            <div className="data-label mb-1">
                              {b.tracking_status === 'CANCELLED_BY_CUSTOMER' ? 'CUSTOMER REASON' : 'EDIT-DRIVEN REFUND'}
                            </div>
                            <div className="p-2 border-2 border-red-300 bg-red-50 text-xs font-mono text-red-700 break-words min-h-[2.5rem]" data-testid={`refund-reason-${b.baggage_tag}`}>
                              {b.cancellation_reason
                                || (b.payment_status === 'refund_pending' && Number(b.pending_refund_amount) > 0
                                  ? `Customer edited booking — refund of ₹${Number(b.pending_refund_amount).toFixed(0)} requested.`
                                  : '—')}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs font-mono mt-3">
                              <div>
                                <div className="data-label text-[10px]">{isEditRefund ? 'REFUND DUE' : cancelled ? 'FULL REFUND' : 'AMOUNT'}</div>
                                <div className="font-bold">₹{refundBaseline.toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="data-label text-[10px]">REFUNDED</div>
                                <div className="font-bold" data-testid={`refund-already-${b.baggage_tag}`}>₹{refunded.toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="data-label text-[10px]">REMAINING</div>
                                <div className="font-bold text-red-700" data-testid={`refund-remaining-${b.baggage_tag}`}>₹{remaining.toFixed(2)}</div>
                              </div>
                            </div>
                          </div>

                          {/* Right — refund form */}
                          <div>
                            {notPaid ? (
                              <div className="p-3 border-2 border-gray-300 bg-gray-50 text-xs font-mono text-gray-600">
                                Booking was not paid — no refund applicable.
                              </div>
                            ) : fullyRefunded ? (
                              <div className="p-3 border-2 border-green-600 bg-green-50 text-xs font-mono text-green-700 font-bold uppercase" data-testid={`refund-done-${b.baggage_tag}`}>
                                Fully refunded
                              </div>
                            ) : (
                              <>
                                <label className="block text-[10px] font-mono uppercase tracking-wide mb-1">AMOUNT (₹) *</label>
                                <input
                                  data-testid={`refund-amount-${b.baggage_tag}`}
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={rowRefundAmounts[b.booking_id] ?? String(remaining)}
                                  onChange={(e) => setRowRefundAmounts((p) => ({ ...p, [b.booking_id]: e.target.value }))}
                                  className="w-full mb-2"
                                  disabled={rowRefundLoading[b.booking_id]}
                                />
                                <label className="block text-[10px] font-mono uppercase tracking-wide mb-1">NOTES (OPTIONAL)</label>
                                <input
                                  data-testid={`refund-notes-${b.baggage_tag}`}
                                  type="text"
                                  value={rowRefundNotes[b.booking_id] || ''}
                                  onChange={(e) => setRowRefundNotes((p) => ({ ...p, [b.booking_id]: e.target.value }))}
                                  placeholder="Internal note"
                                  className="w-full mb-2"
                                  disabled={rowRefundLoading[b.booking_id]}
                                />
                                <button
                                  data-testid={`refund-btn-${b.baggage_tag}`}
                                  onClick={() => handleRowRefund(b)}
                                  disabled={!!rowRefundLoading[b.booking_id]}
                                  className="w-full px-3 py-2 border-2 border-red-700 bg-red-700 text-white hover:bg-red-800 text-xs font-mono uppercase font-bold disabled:opacity-50"
                                >
                                  {rowRefundLoading[b.booking_id] ? 'PROCESSING…' : 'REFUND'}
                                </button>
                              </>
                            )}
                            <RefundErrorNote booking={b} testId={`refund-last-error-${b.baggage_tag}`} />
                            {msg && (
                              <p
                                data-testid={`refund-msg-${b.baggage_tag}`}
                                className={`mt-2 text-[11px] font-mono p-2 border-2 ${msg.type === 'ok' ? 'border-green-700 bg-green-50 text-green-700' : 'border-red-700 bg-red-50 text-red-700'}`}
                              >
                                {msg.text}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'settings' && (
          <div data-testid="settings-tab" className="space-y-6">
            <NotificationSettings />
            <ReportsExportCard />
            <InvoiceResendCard />
            <PurgeOrdersCard />
          </div>
        )}

        {activeTab === 'users' && <RegisteredUsersTab />}
        {activeTab === 'pending-payments' && (
          <PendingPaymentsTab onOpenBooking={async (id) => {
            const b = bookings.find((x) => x.booking_id === id) || await fetchAdminBooking(id);
            if (b) openBookingDetails(b);
          }} />
        )}

        {activeTab === 'support' && (
          <div data-testid="support-tab" className="space-y-4">
            {/* AI-powered bag-finder — used when a customer calls in about a lost bag */}
            <FindBagPanel />

            {/* Status filter pills + counts */}
            <div className="flex flex-wrap gap-2 items-center">
              {['open', 'replied', 'closed', 'all'].map(s => {
                const label = s.toUpperCase();
                const count = s === 'all' ? supportCounts.total : (supportCounts[s] || 0);
                return (
                  <button
                    key={s}
                    data-testid={`support-filter-${s}`}
                    onClick={() => setSupportStatusFilter(s)}
                    className={`px-4 py-2 font-mono uppercase text-xs font-bold border-2 border-black ${
                      supportStatusFilter === s ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    {label} ({count})
                  </button>
                );
              })}
              <button
                data-testid="support-refresh"
                onClick={fetchSupportTickets}
                className="ml-auto px-3 py-2 border-2 border-black bg-white hover:bg-gray-100 font-mono uppercase text-xs font-bold"
              >
                Refresh
              </button>
            </div>

            {supportLoading ? (
              <div className="text-center py-12 font-mono text-sm text-gray-500">Loading tickets…</div>
            ) : supportTickets.length === 0 ? (
              <div data-testid="support-empty" className="text-center py-12 border-2 border-dashed border-gray-300 font-mono text-sm text-gray-500">
                No {supportStatusFilter !== 'all' ? supportStatusFilter : ''} tickets.
              </div>
            ) : (
              <div className="overflow-x-auto border-2 border-black">
                <table className="w-full text-sm">
                  <thead className="bg-black text-white">
                    <tr>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Ticket</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Customer</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Subject</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Booking</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Status</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Received</th>
                      <th className="text-left px-3 py-2 font-mono uppercase text-xs">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supportTickets.map(t => (
                      <tr key={t.ticket_id} data-testid={`support-row-${t.ticket_id}`} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">{t.ticket_id}</td>
                        <td className="px-3 py-2">
                          <div className="font-bold">{t.name}</div>
                          <div className="text-xs text-gray-600">{t.email}</div>
                          {t.mobile && <div className="text-xs font-mono text-gray-500">{t.mobile}</div>}
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate">{t.subject}</td>
                        <td className="px-3 py-2 font-mono text-xs">{t.baggage_tag || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase font-bold border-2 border-black ${
                            t.status === 'open' ? 'bg-[#FF4500] text-black'
                              : t.status === 'replied' ? 'bg-yellow-300 text-black'
                              : 'bg-gray-200 text-gray-700'
                          }`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">
                          {new Date(t.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            data-testid={`support-view-${t.ticket_id}`}
                            onClick={() => setSupportViewTicket(t)}
                            className="text-xs font-bold uppercase text-[#FF4500] hover:underline"
                          >
                            View →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {supportViewTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="support-view-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs font-mono uppercase text-gray-500">Ticket · {supportViewTicket.ticket_id}</div>
                  <div className="text-2xl font-black mt-1">{supportViewTicket.subject}</div>
                </div>
                <button
                  data-testid="support-view-close"
                  onClick={() => setSupportViewTicket(null)}
                  className="text-2xl leading-none font-black hover:text-[#FF4500]"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <div className="text-xs font-mono uppercase text-gray-500">Name</div>
                  <div className="font-bold">{supportViewTicket.name}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-gray-500">Email</div>
                  <a href={`mailto:${supportViewTicket.email}`} className="font-bold text-[#FF4500] hover:underline break-all">{supportViewTicket.email}</a>
                </div>
                {supportViewTicket.mobile && (
                  <div>
                    <div className="text-xs font-mono uppercase text-gray-500">Mobile</div>
                    <div className="font-mono">{supportViewTicket.mobile}</div>
                  </div>
                )}
                {supportViewTicket.baggage_tag && (
                  <div>
                    <div className="text-xs font-mono uppercase text-gray-500">Booking</div>
                    <div className="font-mono font-bold">{supportViewTicket.baggage_tag}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-mono uppercase text-gray-500">Channel</div>
                  <div className="font-mono text-xs">{supportViewTicket.channel}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-gray-500">Received</div>
                  <div className="font-mono text-xs">{new Date(supportViewTicket.created_at).toLocaleString()}</div>
                </div>
              </div>

              <div className="border-2 border-black p-4 bg-[#FFFCF7] mb-4">
                <div className="text-xs font-mono uppercase text-gray-500 mb-2">Message</div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{supportViewTicket.message}</div>
              </div>

              <div className="flex flex-wrap gap-2 items-center border-t-2 border-black pt-4">
                <a
                  href={`mailto:${supportViewTicket.email}?subject=Re: ${encodeURIComponent(supportViewTicket.subject)}%20(${supportViewTicket.ticket_id})`}
                  data-testid="support-reply-email"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-black text-white font-mono uppercase text-xs font-bold hover:bg-[#FF4500] transition-colors"
                >
                  Reply by Email
                </a>
                <div className="ml-auto flex flex-wrap gap-2">
                  {supportViewTicket.status !== 'open' && (
                    <button
                      data-testid="support-set-open"
                      onClick={() => handleSupportStatusChange(supportViewTicket.ticket_id, 'open')}
                      className="px-3 py-2 border-2 border-black bg-white hover:bg-gray-100 font-mono uppercase text-xs font-bold"
                    >
                      Reopen
                    </button>
                  )}
                  {supportViewTicket.status !== 'replied' && (
                    <button
                      data-testid="support-set-replied"
                      onClick={() => handleSupportStatusChange(supportViewTicket.ticket_id, 'replied')}
                      className="px-3 py-2 border-2 border-black bg-yellow-300 hover:bg-yellow-200 font-mono uppercase text-xs font-bold"
                    >
                      Mark Replied
                    </button>
                  )}
                  {supportViewTicket.status !== 'closed' && (
                    <button
                      data-testid="support-set-closed"
                      onClick={() => handleSupportStatusChange(supportViewTicket.ticket_id, 'closed')}
                      className="px-3 py-2 border-2 border-black bg-gray-200 hover:bg-gray-100 font-mono uppercase text-xs font-bold"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreateAgent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="create-agent-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  ADD AGENT
                </h2>
                <button onClick={() => { setShowCreateAgent(false); setAgentError(''); }} className="hover:text-orange-600">
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">AGENT CODE *</label>
                  <input
                    data-testid="new-agent-code"
                    type="text"
                    value={newAgent.agent_code}
                    onChange={(e) => setNewAgent({ ...newAgent, agent_code: e.target.value.toUpperCase() })}
                    placeholder="e.g. MUM-AGENT-02"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NAME *</label>
                  <input
                    data-testid="new-agent-name"
                    type="text"
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    placeholder="Agent full name"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">CONTACT</label>
                  <input
                    data-testid="new-agent-contact"
                    type="text"
                    value={newAgent.contact}
                    onChange={(e) => setNewAgent({ ...newAgent, contact: e.target.value })}
                    placeholder="Phone or email"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">PASSWORD *</label>
                  <input
                    data-testid="new-agent-password"
                    type="password"
                    value={newAgent.password}
                    onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
                    placeholder="Min 4 characters"
                    className="w-full"
                  />
                </div>
                {agentError && (
                  <p data-testid="agent-create-error" className="text-red-600 text-sm font-mono">{agentError}</p>
                )}
                <button
                  data-testid="confirm-create-agent-btn"
                  onClick={handleCreateAgent}
                  disabled={agentLoading || !newAgent.agent_code || !newAgent.name || !newAgent.password}
                  className="btn-primary w-full py-3 disabled:opacity-50"
                >
                  {agentLoading ? 'CREATING...' : 'CREATE AGENT'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Agent Password Modal */}
        {resetPwAgent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="reset-agent-pw-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  RESET AGENT PASSWORD
                </h2>
                <button onClick={() => { setResetPwAgent(null); setNewAgentPassword(''); }} className="hover:text-orange-600"><X size={24} /></button>
              </div>
              <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600">AGENT</div>
                <div className="text-lg font-black font-mono">{resetPwAgent.agent_code}</div>
                <div className="text-xs">{resetPwAgent.name}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NEW PASSWORD</label>
                  <input
                    data-testid="reset-agent-new-password"
                    type="text"
                    value={newAgentPassword}
                    onChange={(e) => setNewAgentPassword(e.target.value)}
                    placeholder="Min 4 characters"
                    className="w-full"
                  />
                </div>
                <button
                  data-testid="confirm-reset-agent-pw-btn"
                  onClick={handleResetAgentPassword}
                  disabled={newAgentPassword.length < 4}
                  className="btn-primary w-full py-3 disabled:opacity-50"
                >
                  RESET PASSWORD
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Partner Modal */}
        {showCreatePartner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="create-partner-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  ADD PARTNER
                </h2>
                <button onClick={() => { setShowCreatePartner(false); setPartnerError(''); }} className="hover:text-orange-600">
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">PARTNER CODE *</label>
                  <input
                    data-testid="new-partner-code"
                    type="text"
                    value={newPartner.partner_code}
                    onChange={(e) => setNewPartner({ ...newPartner, partner_code: e.target.value.toUpperCase() })}
                    placeholder="e.g. BLR-EXPRESS"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NAME *</label>
                  <input
                    data-testid="new-partner-name"
                    type="text"
                    value={newPartner.name}
                    onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                    placeholder="Partner business name"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">CONTACT</label>
                  <input
                    data-testid="new-partner-contact"
                    type="text"
                    value={newPartner.contact}
                    onChange={(e) => setNewPartner({ ...newPartner, contact: e.target.value })}
                    placeholder="Phone or email"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">PASSWORD *</label>
                  <input
                    data-testid="new-partner-password"
                    type="password"
                    value={newPartner.password}
                    onChange={(e) => setNewPartner({ ...newPartner, password: e.target.value })}
                    placeholder="Min 4 characters"
                    className="w-full"
                  />
                </div>
                {partnerError && (
                  <p data-testid="partner-create-error" className="text-red-600 text-sm font-mono">{partnerError}</p>
                )}
                <button
                  data-testid="confirm-create-partner-btn"
                  onClick={handleCreatePartner}
                  disabled={partnerLoading || !newPartner.partner_code || !newPartner.name || !newPartner.password}
                  className="btn-primary w-full py-3 disabled:opacity-50"
                >
                  {partnerLoading ? 'CREATING...' : 'CREATE PARTNER'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Partner Modal */}
        {editPartner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="edit-partner-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  EDIT PARTNER
                </h2>
                <button onClick={() => setEditPartner(null)} className="hover:text-orange-600"><X size={24} /></button>
              </div>
              <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600">CODE</div>
                <div className="text-lg font-black font-mono">{editPartner.partner_code}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NAME</label>
                  <input
                    data-testid="edit-partner-name"
                    type="text"
                    value={editPartnerData.name}
                    onChange={(e) => setEditPartnerData({ ...editPartnerData, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">CONTACT</label>
                  <input
                    data-testid="edit-partner-contact"
                    type="text"
                    value={editPartnerData.contact}
                    onChange={(e) => setEditPartnerData({ ...editPartnerData, contact: e.target.value })}
                    className="w-full"
                  />
                </div>
                <button data-testid="save-partner-btn" onClick={handleSaveEditPartner} className="btn-primary w-full py-3">
                  SAVE CHANGES
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPwPartner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="reset-pw-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  RESET PASSWORD
                </h2>
                <button onClick={() => { setResetPwPartner(null); setNewPassword(''); }} className="hover:text-orange-600"><X size={24} /></button>
              </div>
              <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600">PARTNER</div>
                <div className="text-lg font-black font-mono">{resetPwPartner.partner_code}</div>
                <div className="text-xs">{resetPwPartner.name}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">NEW PASSWORD</label>
                  <input
                    data-testid="reset-new-password"
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 4 characters"
                    className="w-full"
                  />
                </div>
                <p className="text-xs font-mono text-gray-600">
                  Inform the partner of the new password securely. They can change it themselves after login.
                </p>
                <button
                  data-testid="confirm-reset-pw-btn"
                  onClick={handleResetPassword}
                  disabled={newPassword.length < 4}
                  className="btn-primary w-full py-3 disabled:opacity-50"
                >
                  RESET PASSWORD
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Assign Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="bulk-assign-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                  BULK ASSIGN
                </h2>
                <button onClick={() => setShowBulkModal(false)} className="hover:text-orange-600"><X size={24} /></button>
              </div>
              <div className="mb-4 p-3 bg-orange-50 border-2 border-[#FF4500]">
                <div className="text-xs font-mono uppercase text-gray-600">SELECTED</div>
                <div className="text-2xl font-black font-mono">{selectedBookings.size} BOOKING{selectedBookings.size !== 1 ? 'S' : ''}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wide mb-2">ASSIGN TO</label>
                  <select
                    data-testid="bulk-assignee-select"
                    value={bulkAssignValue}
                    onChange={(e) => setBulkAssignValue(e.target.value)}
                    className="w-full"
                  >
                    <option value="">— UNASSIGN (CLEAR) —</option>
                    {partners.filter(p => p.active).length > 0 && (
                      <optgroup label="Partners">
                        {partners.filter(p => p.active).map(p => (
                          <option key={p.partner_id} value={`partner:${p.partner_code}`}>P · {p.partner_code} — {p.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {agents.filter(a => a.active).length > 0 && (
                      <optgroup label="Agents">
                        {agents.filter(a => a.active).map(a => (
                          <option key={a.agent_id} value={`agent:${a.agent_code}`}>A · {a.agent_code} — {a.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <button
                  data-testid="confirm-bulk-assign-btn"
                  onClick={handleBulkAssign}
                  className="btn-primary w-full py-3"
                >
                  {bulkAssignValue ? `ASSIGN TO ${bulkAssignValue.split(':')[1]}` : 'UNASSIGN ALL'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {editBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="card-brutalist bg-white p-8 max-w-md w-full" data-testid="update-status-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>
                UPDATE STATUS
              </h2>
              <button onClick={() => setEditBooking(null)} className="hover:text-orange-600">
                <X size={24} />
              </button>
            </div>
            <div className="mb-4 p-3 bg-gray-100 border-2 border-black">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
              <div className="text-xl font-black font-mono">{editBooking.baggage_tag}</div>
              <div className="text-xs font-mono text-gray-600 mt-2">{editBooking.customer_name}</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">NEW STATUS</label>
                <div data-testid="status-radio-group" className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border-2 border-black p-2 bg-white">
                  {STATUS_PICKER_OPTIONS.map(s => {
                    const cfg = STATUS_CONFIG[s] || {};
                    const checked = newStatus === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        data-testid={`status-radio-${s.toLowerCase()}`}
                        onClick={() => setNewStatus(s)}
                        className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-xs font-mono uppercase font-bold text-left transition-colors ${checked ? 'bg-[#FF4500] text-black' : 'bg-white text-black hover:bg-gray-100'}`}
                      >
                        <span className={`inline-block w-3 h-3 rounded-full border-2 border-black ${checked ? 'bg-black' : 'bg-white'}`}></span>
                        <span className="flex-1">{cfg.label || s}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">NOTES (OPTIONAL)</label>
                <textarea
                  data-testid="status-notes"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Additional details about this status update"
                  rows={3}
                  className="w-full"
                />
              </div>
              {updateError && (
                <p data-testid="update-error" className="text-red-600 text-sm font-mono">{updateError}</p>
              )}
              <button
                data-testid="confirm-update-btn"
                onClick={handleUpdateStatus}
                disabled={updating}
                className="btn-primary w-full py-3 disabled:opacity-50"
              >
                {updating ? 'UPDATING...' : 'UPDATE & NOTIFY CUSTOMER'}
              </button>
              <p className="text-xs text-gray-500 font-mono text-center">
                Status updates trigger WhatsApp/SMS notifications for major changes
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View bag details modal */}
      {viewingBooking && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) closeBookingDetails(); }}
          data-testid="admin-view-bag-details-overlay"
        >
          <div className="card-brutalist bg-white max-w-3xl w-full max-h-[90vh] flex flex-col" data-testid="admin-view-bag-details-modal">
            <div className="flex items-center justify-between p-6 pb-4 border-b-2 border-black bg-white sticky top-0 rounded-t-[inherit]">
              <div>
                <h2 className="text-xl font-black uppercase" style={{ fontFamily: 'Cabinet Grotesk' }}>BAGGAGE DETAILS</h2>
                <p className="text-xs font-mono uppercase text-gray-600">FULL MANIFEST FOR ADMIN REVIEW</p>
              </div>
              <button data-testid="admin-close-bag-details" onClick={closeBookingDetails} aria-label="Close details" className="p-2 -m-2 hover:text-orange-600"><X size={24}/></button>
            </div>
            <div className="overflow-y-auto p-6 pt-4">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gray-100 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGGAGE TAG</div>
                <div className="text-xl font-black font-mono">{viewingBooking.baggage_tag}</div>
              </div>
              <div className="p-3 bg-gray-50 border-2 border-black">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-bold">{viewingBooking.customer_name}</div>
                    {viewingBooking.user_mobile && (
                      <div className="text-xs font-mono text-gray-600 mt-1">{viewingBooking.user_mobile}</div>
                    )}
                  </div>
                  {viewingBooking.user_mobile && (
                    <CallCustomerButton phone={viewingBooking.user_mobile} size="sm" />
                  )}
                </div>
                {viewingBooking.user_id && (
                  <button
                    data-testid="admin-reveal-aadhaar-btn"
                    onClick={() => setAadhaarRevealOpen(true)}
                    className="mt-2 px-2 py-1 border border-gray-400 text-[10px] font-mono uppercase hover:bg-gray-200 flex items-center gap-1 text-gray-600"
                    title="Legacy customers only — Aadhaar is not collected for new registrations"
                  >
                    <Lock size={10}/>VIEW AADHAAR (LEGACY)
                  </button>
                )}
              </div>
            </div>

            <div className="p-3 bg-gray-50 border-2 border-black mb-4">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1"><MapPin size={10}/>ROUTE</div>
              <div className="text-sm font-mono space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">PICKUP — {viewingBooking.pickup_location}</span>
                    <span className="text-gray-600"> ({formatPickupAddress(viewingBooking)})</span>
                  </div>
                  <ViewMapButton booking={viewingBooking} type="pickup" size="sm" />
                </div>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-bold">DROP — {viewingBooking.delivery_location}</span>
                    {formatDeliveryAddress(viewingBooking) && <span className="text-gray-600"> ({formatDeliveryAddress(viewingBooking)})</span>}
                  </div>
                  <ViewMapButton booking={viewingBooking} type="delivery" size="sm" />
                </div>
                <DeliveryOtpNumber booking={viewingBooking} />
              </div>
            </div>

            {(viewingBooking.scheduled_pickup_date || viewingBooking.scheduled_pickup_time) && (
              <div className="p-3 bg-[#FFF7E6] border-2 border-black mb-4" data-testid="admin-scheduled-pickup">
                <div className="text-xs font-mono uppercase text-gray-600 mb-1 flex items-center gap-1">
                  <Calendar size={10}/>SCHEDULED PICKUP
                </div>
                <div className="text-sm font-bold font-mono">
                  {viewingBooking.scheduled_pickup_date || '—'}{viewingBooking.scheduled_pickup_time ? ` · ${formatPickupSlot(viewingBooking.scheduled_pickup_time)}` : ''}
                </div>
              </div>
            )}

            {viewingBooking.tracking_status === 'CANCELLED_BY_CUSTOMER' && (
              <div className="p-3 bg-red-50 border-2 border-red-600 mb-4" data-testid="admin-cancellation-block">
                <div className="text-xs font-mono uppercase text-red-700 mb-1 font-bold flex items-center gap-1">
                  <X size={10}/>CANCELLED BY CUSTOMER
                </div>
                <div className="text-sm font-mono">
                  <span className="text-gray-600">Reason: </span>
                  <span className="font-bold" data-testid="admin-cancellation-reason">{viewingBooking.cancellation_reason || '—'}</span>
                </div>
                {viewingBooking.cancelled_at && (
                  <div className="text-xs font-mono text-gray-600 mt-1">
                    {new Date(viewingBooking.cancelled_at).toLocaleString('en-IN')}
                  </div>
                )}

                {/* Admin Refund Control */}
                {(viewingBooking.payment_status === 'paid' || viewingBooking.payment_status === 'partially_refunded' || viewingBooking.payment_status === 'refunded') && (
                  <div className="mt-3 pt-3 border-t-2 border-dashed border-red-300" data-testid="admin-refund-block">
                    <div className="font-bold uppercase text-red-700 mb-2 text-xs font-mono">REFUND</div>

                    <div className="grid grid-cols-2 gap-2 text-xs font-mono mb-3">
                      <div>
                        <div className="text-gray-600">Booking amount</div>
                        <div className="font-bold">₹{displayAmount(viewingBooking).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Already refunded</div>
                        <div className="font-bold" data-testid="admin-already-refunded">₹{Number(viewingBooking.total_refunded || 0).toFixed(2)}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-600">Remaining refundable</div>
                        <div className="font-bold text-red-700" data-testid="admin-remaining-refundable">
                          ₹{Math.max(0, displayAmount(viewingBooking) - Number(viewingBooking.total_refunded || 0)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <RefundErrorNote booking={viewingBooking} testId="admin-last-refund-error" />

                    {viewingBooking.payment_status !== 'refunded' && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-[10px] font-mono uppercase tracking-wide mb-1">REFUND AMOUNT (₹) *</label>
                          <input
                            data-testid="admin-refund-amount-input"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            placeholder="e.g. 1200.00"
                            className="w-full"
                            disabled={refundLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-mono uppercase tracking-wide mb-1">NOTES (OPTIONAL)</label>
                          <input
                            data-testid="admin-refund-notes-input"
                            type="text"
                            value={refundNotes}
                            onChange={(e) => setRefundNotes(e.target.value)}
                            placeholder="Internal note"
                            className="w-full"
                            disabled={refundLoading}
                          />
                        </div>
                        {refundError && (
                          <p data-testid="admin-refund-error" className="text-red-700 text-xs font-mono p-2 border-2 border-red-700 bg-red-50">{refundError}</p>
                        )}
                        {refundSuccess && (
                          <p data-testid="admin-refund-success" className="text-green-700 text-xs font-mono p-2 border-2 border-green-700 bg-green-50">{refundSuccess}</p>
                        )}
                        <button
                          data-testid="admin-process-refund-btn"
                          onClick={handleProcessRefund}
                          disabled={refundLoading}
                          className="w-full px-3 py-2 border-2 border-red-700 bg-red-700 text-white hover:bg-red-800 text-xs font-mono uppercase font-bold disabled:opacity-50"
                        >
                          {refundLoading ? 'PROCESSING…' : 'PROCESS REFUND'}
                        </button>
                        <p className="text-[10px] font-mono text-gray-600">Refund will be sent to the customer's original Stripe payment method (card / bank account).</p>
                      </div>
                    )}

                    {viewingBooking.payment_status === 'refunded' && (
                      <p className="text-xs font-mono text-green-700 font-bold" data-testid="admin-fully-refunded">FULLY REFUNDED</p>
                    )}

                    {/* Refund history */}
                    {(viewingBooking.refunds || []).length > 0 && (
                      <div className="mt-3 pt-2 border-t border-dashed border-red-300" data-testid="admin-refund-history">
                        <div className="text-[10px] font-mono uppercase text-gray-600 mb-1">HISTORY ({viewingBooking.refunds.length})</div>
                        <div className="space-y-1">
                          {viewingBooking.refunds.map((r, idx) => (
                            <div key={idx} className="text-[11px] font-mono p-2 bg-white border border-red-200">
                              <div className="flex justify-between">
                                <span className="font-bold">₹{Number(r.amount || 0).toFixed(2)}</span>
                                <span className="uppercase text-gray-600" data-testid={`admin-refund-status-${idx}`}>
                                  {(r.parts && r.parts.length
                                    ? (r.parts.every((p) => p.status === 'processed') ? 'processed' : r.parts.some((p) => p.status === 'failed') ? 'failed' : 'pending')
                                    : r.status) || 'pending'}{r.provider === 'razorpay' ? ' · razorpay' : ''}
                                </span>
                              </div>
                              <div className="text-gray-600 break-all">{r.refund_id}</div>
                              {r.notes && <div className="text-gray-700 mt-0.5">"{r.notes}"</div>}
                              {r.processed_at && <div className="text-gray-500 mt-0.5">{new Date(r.processed_at).toLocaleString('en-IN')}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {viewingBooking.last_refund_error && (
                      <p className="mt-2 text-[11px] font-mono text-red-700 p-2 bg-white border border-red-300">
                        Last attempt failed: {viewingBooking.last_refund_error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-sm font-black uppercase mb-3 font-mono">
                BAGS ({(viewingBooking.luggage_details?.bags || []).length})
              </h3>
              <div className="space-y-4">
                {(viewingBooking.luggage_details?.bags || []).map((bag, idx) => (
                  <div key={idx} className="border-2 border-black p-4 bg-white" data-testid={`admin-bag-detail-${idx}`}>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-dashed border-gray-300">
                      <div className="font-mono uppercase font-black text-base">BAG #{idx + 1}</div>
                      <div className="px-2 py-1 bg-black text-white text-xs font-mono uppercase">{(bag.bag_type || 'BAG').toUpperCase()}</div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3 text-xs">
                      <div><div className="data-label flex items-center gap-1"><Tag size={10}/>BRAND/COMPANY</div><div className="font-bold">{bag.brand || '—'}</div></div>
                      <div><div className="data-label">COLOR</div><div className="font-bold">{bag.color || '—'}</div></div>
                      <div><div className="data-label">MATERIAL</div><div className="font-bold uppercase">{(bag.material || 'soft_fabric').replace('_', ' ')}</div></div>
                      <div><div className="data-label flex items-center gap-1"><Weight size={10}/>WEIGHT</div><div className="font-bold">{bag.weight} KG</div></div>
                      <div><div className="data-label flex items-center gap-1"><Ruler size={10}/>DIMENSIONS</div><div className="font-bold">{bag.dimensions || '—'}</div></div>
                      <div><div className="data-label flex items-center gap-1"><Shield size={10}/>HANDLING</div><div className="font-bold uppercase">{bag.handling_type || 'standard'}</div></div>
                      <div><div className="data-label flex items-center gap-1"><Lock size={10}/>LOCK</div><div className="font-bold uppercase">{bag.lock_type || 'none'}</div></div>
                      <div><div className="data-label">DECLARED VALUE</div><div className="font-bold">₹{(bag.declared_value || 0).toFixed(0)}</div></div>
                      <div className="col-span-2 sm:col-span-1"><div className="data-label">DISTINGUISHING MARKS</div><div className="font-bold">{bag.distinguishing_marks || '—'}</div></div>
                    </div>
                    <div>
                      <div className="data-label mb-2 flex items-center gap-1"><ImageIcon size={10}/>CUSTOMER PHOTOS ({(bag.images || []).length})</div>
                      {(bag.images || []).length === 0 ? (
                        <div className="text-xs font-mono text-gray-500 italic p-3 border-2 border-dashed border-gray-300 text-center">
                          No photos uploaded by customer
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {(bag.images || []).map((img, imgIdx) => (
                            <button key={imgIdx} type="button" onClick={() => openPhoto(bag.images || [], imgIdx)} data-testid={`admin-bag-${idx}-photo-${imgIdx}`} className="block border-2 border-black overflow-hidden hover:shadow-lg cursor-zoom-in" title="Click to enlarge">
                              <img src={img} alt={`Bag ${idx + 1} photo ${imgIdx + 1}`} className="w-full h-24 object-cover"/>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3 bg-[#FFF7E6] border-2 border-black flex items-center justify-between gap-3">
              <div className="text-xs font-mono uppercase font-bold">TOTAL CHARGED</div>
              <div className="text-right">
                <div className="text-xl font-black font-mono" data-testid="admin-total-charged">{formatInr(displayAmount(viewingBooking))}</div>
                {pendingAmountNote(viewingBooking) && (
                  <div className="text-[10px] font-mono text-gray-700" data-testid="admin-total-charged-note">{pendingAmountNote(viewingBooking)}</div>
                )}
              </div>
            </div>
            {viewingBooking.payment_status !== 'pending' && (
              <RecalculatePaymentCard bookingId={viewingBooking.booking_id} onDone={refreshViewingBooking} />
            )}
            <InsuranceConsentPanel booking={viewingBooking} />
            </div>
          </div>
        </div>
      )}

      {/* Printable baggage tag (hidden on screen, shown only when window.print() is triggered) */}
      {printingBooking && (
        <BaggageTagPrint booking={printingBooking.booking} customer={printingBooking.customer} />
      )}

      {/* Aadhaar Reveal Modal (gated by reason + audit log) */}
      {aadhaarRevealOpen && viewingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="card-brutalist bg-white p-6 max-w-md w-full" data-testid="admin-aadhaar-reveal-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                <Lock size={18} />REVEAL FULL AADHAAR
              </h3>
              <button data-testid="admin-aadhaar-close" onClick={() => { setAadhaarRevealOpen(false); setAadhaarReason(''); setAadhaarRevealed(''); setAadhaarError(''); }} className="hover:text-orange-600 disabled:opacity-50" disabled={aadhaarLoading}>
                <X size={20} />
              </button>
            </div>
            <div className="mb-4 p-3 bg-yellow-50 border-2 border-yellow-700 text-[11px] font-mono text-yellow-900">
              ⚠ Every reveal is logged in the compliance audit trail with your admin token, the reason you provide and a timestamp.
            </div>

            <div className="mb-3">
              <div className="text-xs font-mono uppercase text-gray-600 mb-1">CUSTOMER</div>
              <div className="font-bold">{viewingBooking.customer_name}</div>
            </div>

            {!aadhaarRevealed ? (
              <>
                <label className="block text-xs font-mono uppercase tracking-wide mb-2">
                  REASON (min 5 characters) <span className="text-red-600">*</span>
                </label>
                <textarea
                  data-testid="admin-aadhaar-reason-input"
                  value={aadhaarReason}
                  onChange={(e) => setAadhaarReason(e.target.value)}
                  rows={3}
                  className="w-full"
                  placeholder="e.g. Identity verification at pickup point Bangalore"
                  disabled={aadhaarLoading}
                  autoFocus
                />
                {aadhaarError && (
                  <p data-testid="admin-aadhaar-error" className="mt-2 text-red-700 text-xs font-mono p-2 border-2 border-red-700 bg-red-50">{aadhaarError}</p>
                )}
                <div className="flex gap-3 mt-4">
                  <button
                    data-testid="admin-aadhaar-cancel"
                    onClick={() => { setAadhaarRevealOpen(false); setAadhaarReason(''); setAadhaarError(''); }}
                    disabled={aadhaarLoading}
                    className="btn-secondary px-4 py-2 text-sm flex-1 disabled:opacity-50"
                  >
                    CANCEL
                  </button>
                  <button
                    data-testid="admin-aadhaar-confirm"
                    onClick={handleRevealAadhaar}
                    disabled={aadhaarLoading}
                    className="btn-primary px-4 py-2 text-sm flex-1 disabled:opacity-50"
                  >
                    {aadhaarLoading ? 'REVEALING…' : 'REVEAL'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 bg-black text-white border-2 border-black text-center font-mono">
                  <div className="text-[10px] uppercase text-gray-400 mb-1">FULL AADHAAR</div>
                  <div className="text-2xl font-bold tracking-widest" data-testid="admin-aadhaar-value">{aadhaarRevealed.replace(/(\d{4})(?=\d)/g, '$1 ')}</div>
                </div>
                <p className="mt-3 text-[11px] font-mono text-gray-600">This reveal has been logged. Close this dialog when done — the value is not retained on screen after closing.</p>
                <button
                  data-testid="admin-aadhaar-done"
                  onClick={() => { setAadhaarRevealOpen(false); setAadhaarReason(''); setAadhaarRevealed(''); setAadhaarError(''); }}
                  className="btn-primary w-full px-4 py-2 text-sm mt-3"
                >
                  DONE
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {photoGallery}
    </div>
  );
};

export default Admin;
